import { useEffect, useState } from "react";

/** Threshold (in pixels from viewport top) to consider a chart as "active" */
const STICKY_THRESHOLD_PX = 50;

/** Selector for the scrollable parent container */
const SCROLL_CONTAINER_SELECTOR = ".single-order-layout--content";

/** Selector for chart elements within the container */
const CHART_ELEMENT_SELECTOR = "[id='chart']";

interface ChartPosition {
  index: number;
  top: number;
  bottom: number;
  element: Element;
}

interface StickyBarDetails {
  chartName: string;
  top: number;
}

/**
 * Extracts position data for all chart elements within the container
 * @param container - The parent container element containing chart elements
 * @returns Array of chart positions with their bounding rect data
 */
const getChartPositions = (container: HTMLDivElement): ChartPosition[] => {
  const charts = Array.from(container.querySelectorAll(CHART_ELEMENT_SELECTOR));

  return charts.map((chart, index) => {
    const rect = chart.getBoundingClientRect();
    return {
      index,
      top: rect.top,
      bottom: rect.bottom,
      element: chart,
    };
  });
};

/**
 * Finds the chart that is currently at or near the top of the viewport.
 * Prioritizes charts that have scrolled past the threshold (negative/low top values),
 * selecting the one closest to the viewport top.
 * If no charts are past the threshold, returns the chart closest to the top.
 *
 * @param chartPositions - Array of chart position data
 * @returns The chart position closest to the viewport top, or null if no charts exist
 */
const findActiveChart = (
  chartPositions: ChartPosition[],
): ChartPosition | null => {
  if (chartPositions.length === 0) return null;

  // Charts that have scrolled past or are at the sticky threshold
  const chartsAtOrPastThreshold = chartPositions.filter(
    (p) => p.top <= STICKY_THRESHOLD_PX,
  );

  if (chartsAtOrPastThreshold.length > 0) {
    // Return the one with the highest top value (least scrolled past)
    return chartsAtOrPastThreshold.reduce((a, b) => (a.top > b.top ? a : b));
  }

  // No charts past threshold - return the one closest to the top
  return chartPositions.reduce((a, b) => (a.top < b.top ? a : b));
};

/**
 * Extracts the chart name from a chart element's data attribute
 * @param element - The chart DOM element
 * @returns The chart name or empty string if not found
 */
const getChartName = (element: Element): string => {
  return element.getAttribute("data-chart-name") ?? "";
};

/**
 * Hook that tracks scroll position and determines which chart should be
 * displayed in the sticky header bar based on viewport position.
 *
 * @param visitDatChartReference - Ref to the container holding all chart elements
 * @returns Object containing sticky bar details (chart name and top position)
 */
export const useShowStickyBar = (
  visitDatChartReference: React.RefObject<HTMLDivElement>,
) => {
  const [stickyBarDetails, setStickyBarDetails] =
    useState<StickyBarDetails | null>(null);

  useEffect(() => {
    const scrollContainer = document.querySelector(SCROLL_CONTAINER_SELECTOR);

    /**
     * Calculates which chart is currently active based on scroll position
     * and updates the sticky bar details accordingly
     */
    const updateActiveChart = () => {
      if (!visitDatChartReference.current) return;

      const chartPositions = getChartPositions(visitDatChartReference.current);
      const activeChart = findActiveChart(chartPositions);

      if (activeChart) {
        setStickyBarDetails({
          chartName: getChartName(activeChart.element),
          top: activeChart.top,
        });
      }
    };

    scrollContainer?.addEventListener("scroll", updateActiveChart);
    window.addEventListener("resize", updateActiveChart);

    // Calculate initial active chart on mount
    updateActiveChart();

    return () => {
      scrollContainer?.removeEventListener("scroll", updateActiveChart);
      window.removeEventListener("resize", updateActiveChart);
    };
  }, [visitDatChartReference]);

  return { stickyBarDetails, setStickyBarDetails };
};
